import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000,
});

// Request interceptor — attach access token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — auto-refresh on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Attempt refresh on 401 or 403 "Not authenticated" — and not already retrying
    const is401 = error.response?.status === 401;
    const is403NotAuth = error.response?.status === 403 && error.response?.data?.detail === 'Not authenticated';
    if ((is401 || is403NotAuth) && !originalRequest._retry) {
      // Don't refresh on login/register/refresh endpoints
      const skipPaths = ['/api/auth/login', '/api/auth/register', '/api/auth/refresh', '/api/auth/google-callback', '/api/super-admin/login'];
      if (skipPaths.some(p => originalRequest.url?.includes(p))) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        isRefreshing = false;
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: refreshToken,
        });
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        processQueue(null, data.access_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle account lockout — 403 with lockout message forces logout
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('Account temporarily locked')) {
        localStorage.clear();
        window.location.href = `/login?locked=${encodeURIComponent(detail)}`;
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
